import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { AndrichLogo } from "../components/site/Logo";
import {
  Package, MessageSquare, LogOut, Plus, Pencil, Trash2, Search, Loader2, ExternalLink, Upload,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const emptyProduct = { name: "", category: "", form: "", composition: "", description: "", image_url: "" };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  async function uploadImage(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const absolute = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      setForm((f) => ({ ...f, image_url: absolute }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  useEffect(() => {
    if (!localStorage.getItem("andrich_admin_token")) { nav("/admin/login"); return; }
    loadAll();
  }, []); // eslint-disable-line

  async function loadAll() {
    setLoading(true);
    try {
      const [p, i] = await Promise.all([
        api.get("/products"),
        api.get("/admin/inquiries"),
      ]);
      setProducts(p.data);
      setInquiries(i.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("andrich_admin_token");
        nav("/admin/login");
        return;
      }
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }

  function logout() {
    localStorage.removeItem("andrich_admin_token");
    localStorage.removeItem("andrich_admin_user");
    nav("/admin/login");
  }

  function openCreate() { setEditing(null); setForm(emptyProduct); setDialogOpen(true); }
  function openEdit(p) { setEditing(p); setForm({ ...emptyProduct, ...p }); setDialogOpen(true); }

  async function saveProduct(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", form);
        toast.success("Product created");
      }
      setDialogOpen(false);
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function deleteProduct(p) {
    if (!confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deleteInquiry(i) {
    if (!confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${i.id}`);
      toast.success("Inquiry deleted");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const filteredProducts = products.filter((p) => {
    const t = q.trim().toLowerCase();
    return !t || p.name.toLowerCase().includes(t) || p.category?.toLowerCase().includes(t);
  });

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      <aside className="hidden lg:flex flex-col w-64 bg-slate-950 text-slate-200 min-h-screen sticky top-0">
        <div className="p-6 bg-white/95">
          <AndrichLogo />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => setTab("products")} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${tab === "products" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`} data-testid="admin-tab-products">
            <Package size={16} /> Products <span className="ml-auto text-xs opacity-60">{products.length}</span>
          </button>
          <button onClick={() => setTab("inquiries")} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${tab === "inquiries" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`} data-testid="admin-tab-inquiries">
            <MessageSquare size={16} /> Inquiries <span className="ml-auto text-xs opacity-60">{inquiries.length}</span>
          </button>
        </nav>
        <div className="p-4 space-y-2">
          <Link to="/" className="flex items-center gap-2 text-xs text-slate-400 hover:text-white" data-testid="admin-view-site"><ExternalLink size={14} /> View website</Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-slate-400 hover:text-white" data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 lg:px-10 py-4 flex items-center justify-between sticky top-0 z-30">
          <div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-slate-400 font-medium">Admin Console</div>
            <h1 className="font-display text-xl font-semibold text-slate-900">
              {tab === "products" ? "Product Catalogue" : "Contact Inquiries"}
            </h1>
          </div>
          <div className="flex items-center gap-3 lg:hidden">
            <button onClick={() => setTab(tab === "products" ? "inquiries" : "products")} className="btn-secondary text-xs py-2">
              {tab === "products" ? "Inquiries" : "Products"}
            </button>
            <button onClick={logout} className="text-xs text-slate-500" data-testid="admin-logout-mobile"><LogOut size={16} /></button>
          </div>
        </header>

        <main className="px-6 lg:px-10 py-8">
          {loading ? (
            <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-blue-900" /></div>
          ) : tab === "products" ? (
            <div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-6">
                <div className="relative max-w-md flex-1">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm outline-none focus:border-blue-400" data-testid="admin-product-search" />
                </div>
                <button onClick={openCreate} className="btn-primary text-sm" data-testid="admin-product-create">
                  <Plus size={16} className="mr-1.5" /> Add Product
                </button>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[0.7rem] tracking-widest uppercase text-slate-500">
                      <tr>
                        <th className="px-6 py-3">Name</th>
                        <th className="px-6 py-3">Category</th>
                        <th className="px-6 py-3">Form</th>
                        <th className="px-6 py-3">Composition</th>
                        <th className="px-6 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredProducts.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50" data-testid={`admin-product-row-${p.name.toLowerCase().replace(/\s|\//g,"-")}`}>
                          <td className="px-6 py-3 font-medium text-slate-900">{p.name}</td>
                          <td className="px-6 py-3 text-slate-600">{p.category}</td>
                          <td className="px-6 py-3 text-slate-600">{p.form}</td>
                          <td className="px-6 py-3 text-slate-500 max-w-md truncate">{p.composition}</td>
                          <td className="px-6 py-3 text-right whitespace-nowrap">
                            <button onClick={() => openEdit(p)} className="inline-flex items-center gap-1 text-xs text-blue-900 hover:text-blue-700 mr-3" data-testid={`admin-product-edit-${p.name.toLowerCase().replace(/\s|\//g,"-")}`}><Pencil size={13}/> Edit</button>
                            <button onClick={() => deleteProduct(p)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-500" data-testid={`admin-product-delete-${p.name.toLowerCase().replace(/\s|\//g,"-")}`}><Trash2 size={13}/> Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-inquiries-list">
              {inquiries.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500">
                  <MessageSquare className="mx-auto text-slate-300" size={28} />
                  <p className="mt-3">No inquiries yet. Form submissions will appear here.</p>
                </div>
              ) : inquiries.map((i) => (
                <article key={i.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`admin-inquiry-${i.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display text-lg font-semibold text-slate-900">{i.name}</div>
                      <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-3">
                        <a href={`mailto:${i.email}`} className="hover:text-blue-900">{i.email}</a>
                        {i.phone && <span>{i.phone}</span>}
                        <span>{new Date(i.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <button onClick={() => deleteInquiry(i)} className="text-xs text-red-600 hover:text-red-500 inline-flex items-center gap-1"><Trash2 size={13}/> Delete</button>
                  </div>
                  {i.subject && <div className="mt-4 text-sm font-medium text-slate-900">{i.subject}</div>}
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{i.message}</p>
                </article>
              ))}
            </div>
          )}
        </main>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Product" : "New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProduct} className="space-y-3">
            {["name","category","form"].map((f) => (
              <label key={f} className="block">
                <span className="text-[0.7rem] tracking-[0.22em] uppercase text-slate-500 font-medium">{f.replace("_"," ")}</span>
                <input value={form[f] || ""} onChange={(e) => setForm({...form,[f]:e.target.value})} required={f==="name"||f==="category"} className="mt-1.5 w-full px-4 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-blue-400" data-testid={`admin-form-${f}`} />
              </label>
            ))}
            <div>
              <span className="text-[0.7rem] tracking-[0.22em] uppercase text-slate-500 font-medium">Product Image</span>
              <div className="mt-1.5 flex items-start gap-3">
                {form.image_url ? (
                  <img src={form.image_url} alt="Preview" className="h-20 w-20 rounded-lg object-cover border border-slate-200" data-testid="admin-form-image-preview" />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-slate-100 border border-dashed border-slate-300 grid place-items-center text-slate-400 text-xs">No image</div>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadImage(e.target.files?.[0])}
                    className="hidden"
                    data-testid="admin-form-image-file"
                  />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary text-xs py-2 px-3 w-full sm:w-auto" data-testid="admin-form-image-upload">
                    {uploading ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Uploading…</> : <><Upload size={12} className="mr-1.5" /> Upload image (max 5 MB)</>}
                  </button>
                  <input
                    type="text"
                    value={form.image_url || ""}
                    onChange={(e) => setForm({...form, image_url: e.target.value})}
                    placeholder="…or paste an image URL"
                    className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs outline-none focus:border-blue-400"
                    data-testid="admin-form-image_url"
                  />
                </div>
              </div>
            </div>
            <label className="block">
              <span className="text-[0.7rem] tracking-[0.22em] uppercase text-slate-500 font-medium">Composition</span>
              <textarea value={form.composition || ""} onChange={(e) => setForm({...form, composition: e.target.value})} rows={3} className="mt-1.5 w-full px-4 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-blue-400 resize-none" data-testid="admin-form-composition" />
            </label>
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => setDialogOpen(false)} className="btn-secondary text-sm py-2">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary text-sm py-2" data-testid="admin-product-save">
                {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : editing ? "Save Changes" : "Create Product"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
